import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type VerifyResult, verifyPlan } from "@/services/testing";
import { useTestingStore } from "@/stores/testingStore";

/**
 * Pre-flight VERIFY: run the plan headless best-of-N against the live org before the user runs it.
 *
 * On success we do two things so the verdict actually shows:
 *  1. Patch the verdict onto the in-store proposal snapshot the chat card renders (setPlanVerification)
 *     — the card reads the SSE-streamed `proposedPlan`, not a query, so cache invalidation alone left
 *     the badge stuck on "Verify" after the run finished.
 *  2. Invalidate the plan/list/run queries so any query-backed view (sidebar dot, history) refreshes.
 */
export function useVerifyPlan() {
	const queryClient = useQueryClient();
	const setPlanVerification = useTestingStore((s) => s.setPlanVerification);
	return useMutation<VerifyResult, Error, { planId: string; attempts?: number }>({
		mutationFn: (args) => verifyPlan(args),
		onSuccess: (data, variables) => {
			setPlanVerification(variables.planId, {
				verified: data.verified,
				passed: data.passed,
				total: data.total,
				flaky: data.flaky,
				at: new Date().toISOString(),
			});
		},
		onSettled: (_data, _err, variables) => {
			void queryClient.invalidateQueries({ queryKey: ["testingPlan", variables.planId] });
			void queryClient.invalidateQueries({ queryKey: ["testingPlans"] });
			void queryClient.invalidateQueries({ queryKey: ["testingRuns"] });
		},
	});
}
